import {
  FeatureFlagGroup,
  FeatureFlagTeam,
  FlagEnablementStatus,
  ReleaseGroupName,
} from '@/types/schema';
import { formatEnumCase } from '@/utils/formatting/strings';

import {
  AdminFeatureFlagsPage_FeatureFlagFragment,
  AdminFeatureFlagsPage_ReleaseGroupFragment,
} from './graphql/AdminFeatureFlagsPage.generated';

export type GroupByField = 'group' | 'team' | 'owner';

export const GROUP_BY_OPTIONS = [
  { display: 'Group', value: 'group' as const },
  { display: 'Team', value: 'team' as const },
  { display: 'Owner', value: 'owner' as const },
];

export interface ReleaseGroupOverrideSummary {
  id: string;
  name: ReleaseGroupName;
}

export interface FeatureFlagRow {
  id: string;
  name: string;
  description?: string | null;
  owner: string;
  team?: FeatureFlagTeam | null;
  group?: FeatureFlagGroup | null;
  enablementStatus: FlagEnablementStatus;
  onReleaseGroups: ReleaseGroupOverrideSummary[];
  offReleaseGroups: ReleaseGroupOverrideSummary[];
  createdAt: Date;
  flag: AdminFeatureFlagsPage_FeatureFlagFragment;
}

interface EvaluationInput {
  name: string;
  flagEnablementStatus: FlagEnablementStatus;
  flag: AdminFeatureFlagsPage_FeatureFlagFragment;
}

// Variation names emitted by the backend for boolean flag overrides; rules
// using TRUE_VARIATION enable the flag for the scope, FALSE_VARIATION rules
// explicitly disable it. Strings must match
// server/pkg/featureflag.TrueVariation / FalseVariation.
const TRUE_VARIATION = 'variation_true';

export function mapEvaluationToRow(input: EvaluationInput): FeatureFlagRow {
  const { flag, flagEnablementStatus } = input;
  const onReleaseGroups: ReleaseGroupOverrideSummary[] = [];
  const offReleaseGroups: ReleaseGroupOverrideSummary[] = [];
  for (const rule of flag.queryRules ?? []) {
    if (!rule.releaseGroup) continue;
    const summary = {
      id: rule.releaseGroup.id,
      name: rule.releaseGroup.name,
    };
    if (rule.flagVariationName === TRUE_VARIATION) {
      onReleaseGroups.push(summary);
    } else {
      offReleaseGroups.push(summary);
    }
  }

  return {
    id: flag.id,
    name: flag.flagName,
    description: flag.flagDescription,
    owner: flag.ownedBy,
    team: flag.team,
    group: flag.group,
    enablementStatus: flagEnablementStatus,
    onReleaseGroups,
    offReleaseGroups,
    createdAt: new Date(flag.createdAt),
    flag,
  };
}

const UNCATEGORIZED_KEY = '__uncategorized__';
const UNCATEGORIZED_LABEL = 'Uncategorized';

export interface FeatureFlagGroupSection {
  key: string;
  label: string;
  rows: FeatureFlagRow[];
}

export function groupRows(
  rows: FeatureFlagRow[],
  groupBy: GroupByField
): FeatureFlagGroupSection[] {
  const sections = new Map<string, FeatureFlagGroupSection>();

  for (const row of rows) {
    const rawValue = row[groupBy];
    const key = rawValue || UNCATEGORIZED_KEY;
    let label = groupBy === 'owner' ? 'Unassigned' : UNCATEGORIZED_LABEL;
    if (groupBy === 'team' && row.team) {
      label = formatTeamLabel(row.team);
    } else if (row.group && groupBy === 'group') {
      label = formatEnumCase(row.group);
    } else if (groupBy === 'owner' && row.owner) {
      label = row.owner;
    }

    let section = sections.get(key);
    if (!section) {
      section = { key, label, rows: [] };
      sections.set(key, section);
    }
    section.rows.push(row);
  }

  const ordered = Array.from(sections.values()).sort((a, b) => {
    // Uncategorized always last
    if (a.key === UNCATEGORIZED_KEY) return 1;
    if (b.key === UNCATEGORIZED_KEY) return -1;
    return a.label.localeCompare(b.label);
  });

  ordered.forEach((section) => {
    section.rows.sort((a, b) => a.name.localeCompare(b.name));
  });

  return ordered;
}

export function getReleaseGroupsFromQuery(
  edges:
    | Array<{
        node?: AdminFeatureFlagsPage_ReleaseGroupFragment | null;
      } | null>
    | null
    | undefined
): AdminFeatureFlagsPage_ReleaseGroupFragment[] {
  if (!edges) return [];
  const result: AdminFeatureFlagsPage_ReleaseGroupFragment[] = [];
  for (const edge of edges) {
    if (edge?.node) result.push(edge.node);
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

export function formatTeamLabel(team: FeatureFlagTeam): string {
  switch (team) {
    case FeatureFlagTeam.T1Tribeta:
    case FeatureFlagTeam.WealthStrategies:
      return 'Wealth Strategies';
    case FeatureFlagTeam.T2Arco:
    case FeatureFlagTeam.Platform:
      return 'Platform';
    case FeatureFlagTeam.TrustAdmin:
      return 'Trust Admin';
  }
}

export function formatEnablementStatus(status: FlagEnablementStatus): string {
  switch (status) {
    case FlagEnablementStatus.OnForEnv:
      return 'On for all';
    case FlagEnablementStatus.OnForTenant:
      return 'On for tenant';
    case FlagEnablementStatus.OnForReleaseGroup:
      return 'On for release group';
    case FlagEnablementStatus.Off:
      return 'Off';
    case FlagEnablementStatus.OffForTenant:
      return 'Off for tenant';
    case FlagEnablementStatus.OffForReleaseGroup:
      return 'Off for release group';
    case FlagEnablementStatus.ClearTenant:
      return 'Cleared for tenant';
    case FlagEnablementStatus.ClearReleaseGroup:
      return 'Cleared release group';
    default:
      return formatEnumCase(status);
  }
}
